import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useProperty, usePropertyMutations } from '../hooks/useCRM';
import { usePropertiesList } from '../hooks/useProperties';
import PropertyCard from '../components/PropertyCard';
import PropertyForm from '../components/PropertyForm';
import { useToast } from '../components/Toast';
import { useConfirm } from '../hooks/useConfirm';
import PropertiesAdvancedFilters from '../components/properties/PropertiesAdvancedFilters';
import '../crm.css';
import { crmBasePath } from '../utils/crmEnv';
import { uploadFile } from '../utils/crmApi';

const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'available', label: 'Available' },
    { value: 'hold', label: 'Hold' },
    { value: 'booked', label: 'Booked' },
    { value: 'sold', label: 'Sold' },
    { value: 'inactive', label: 'Inactive' },
];

const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'plot', label: 'Plot' },
    { value: 'flat', label: 'Flat' },
    { value: 'villa', label: 'Villa' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'farm', label: 'Farm' },
    { value: 'other', label: 'Other' },
];

const defaultFilters = {
    q: '',
    status: '',
    type: '',
    city: '',
    minPrice: '',
    maxPrice: '',
    minArea: '',
    maxArea: '',
    tags: '',
    sort: 'createdAt',
    dir: 'desc',
    page: 1,
    pageSize: 24,
};

const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
            const base64 = result.split(',')[1] || '';
            resolve(base64);
        } else {
            resolve('');
        }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
});

function PropertiesPage() {
    const history = useHistory();
    const { id: routePropertyId } = useParams();
    const basePath = crmBasePath();

    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingProperty, setEditingProperty] = useState(null);

    const {
        properties,
        total,
        loading,
        error,
        filters,
        setFilter,
        searchInput,
        setSearchInput,
        activeFilters,
        clearFilters,
        refetch,
    } = usePropertiesList(defaultFilters);
    const { property: routeProperty } = useProperty(routePropertyId);
    const { create, update, remove, loading: mutationLoading } = usePropertyMutations();
    const { showSuccess, showError } = useToast();
    const { confirm, ConfirmDialog } = useConfirm();

    useEffect(() => {
        if (!routePropertyId) return;
        if (routeProperty) {
            setEditingProperty(routeProperty);
            setShowModal(true);
        }
    }, [routePropertyId, routeProperty]);

    useEffect(() => {
        const syncOnReturn = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            refetch();
        };
        window.addEventListener('focus', syncOnReturn);
        document.addEventListener('visibilitychange', syncOnReturn);
        return () => {
            window.removeEventListener('focus', syncOnReturn);
            document.removeEventListener('visibilitychange', syncOnReturn);
        };
    }, [refetch]);

    const handleFilterChange = (key, value) => {
        setFilter(key, value);
    };

    const handleAdd = () => {
        setEditingProperty(null);
        setShowModal(true);
    };

    const handleEdit = (property) => {
        history.push(`${basePath}/properties/${property.id}`);
    };

    const handleDelete = async (id) => {
        const isConfirmed = await confirm({
            title: 'Delete Property',
            message: 'Are you sure you want to delete this property? This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'danger',
        });

        if (!isConfirmed) return;

        try {
            await remove(id);
            showSuccess('Property deleted successfully');
            if (routePropertyId === id) {
                history.push(`${basePath}/properties`);
            }
            refetch();
        } catch (err) {
            console.error('Failed to delete property:', err);
            showError('Failed to delete property');
        }
    };

    const handleSubmit = async (formData) => {
        const { imageFiles = [], imageUrls = [], ...payload } = formData || {};
        try {
            let savedProperty = null;
            if (editingProperty) {
                savedProperty = await update(editingProperty.id, payload);
                showSuccess('Property updated successfully');
            } else {
                savedProperty = await create(payload);
                showSuccess('Property created successfully');
            }

            const propertyId = editingProperty?.id || savedProperty?.id;
            if (propertyId && imageFiles.length > 0) {
                const uploaded = await Promise.all(
                    imageFiles.map(async (file) => {
                        const base64Data = await readFileAsBase64(file);
                        return uploadFile({
                            entityType: 'property',
                            entityId: propertyId,
                            fileName: file.name,
                            contentType: file.type || 'application/octet-stream',
                            base64Data,
                        });
                    })
                );

                const uploadedUrls = uploaded
                    .map((meta) => meta.publicUrl || meta.url || meta.b2Path)
                    .filter(Boolean);
                const nextImageUrls = [...imageUrls, ...uploadedUrls].filter(Boolean);
                if (nextImageUrls.length > 0) {
                    await update(propertyId, { imageUrls: nextImageUrls });
                }
            }

            setShowModal(false);
            setEditingProperty(null);
            if (routePropertyId) {
                history.push(`${basePath}/properties`);
            }
            refetch();
        } catch (err) {
            console.error('Failed to save property:', err);
            showError('Failed to save property');
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingProperty(null);
        if (routePropertyId) {
            history.push(`${basePath}/properties`);
        }
    };

    const hasActiveFilters = useMemo(() => (
        filters.q ||
        filters.status ||
        filters.type ||
        filters.city ||
        filters.minPrice ||
        filters.maxPrice ||
        filters.minArea ||
        filters.maxArea ||
        filters.tags
    ), [filters]);

    const pageSize = 24;
    const startItem = (filters.page - 1) * pageSize + 1;
    const endItem = Math.min(filters.page * pageSize, total || 0);
    const totalPages = Math.ceil((total || 0) / pageSize);

    return (
        <div className="crm-container">
            <div className="crm-page-header">
                <h1 className="crm-page-title">
                    Properties
                    <span className="count-badge">{total || 0}</span>
                </h1>
                <button className="crm-btn crm-btn-primary" onClick={handleAdd}>
                    + Add Property
                </button>
            </div>

            <div className="crm-filter-toolbar">
                <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '320px' }}>
                    <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--crm-gray-400)',
                    }}>
                        ?
                    </span>
                    <input
                        type="text"
                        className="crm-input crm-search-input"
                        placeholder="Search properties..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        style={{ paddingLeft: '36px' }}
                    />
                </div>

                <select
                    className="crm-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    style={{ minWidth: '150px' }}
                >
                    {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                <select
                    className="crm-select"
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    style={{ minWidth: '150px' }}
                >
                    {typeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                <button className="crm-btn crm-btn-secondary" onClick={() => setShowAdvanced(true)}>
                    Advanced
                </button>

                <button className="crm-btn crm-btn-primary" onClick={handleAdd}>
                    + Add Property
                </button>
            </div>

            {hasActiveFilters && (
                <div className="crm-filter-chips">
                    {activeFilters.map((chip) => (
                        <button
                            key={chip.key}
                            type="button"
                            className="crm-filter-chip"
                            onClick={() => handleFilterChange(chip.key, '')}
                        >
                            {chip.label}
                            <span>×</span>
                        </button>
                    ))}
                    <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={clearFilters}>
                        Clear
                    </button>
                </div>
            )}

            {loading ? (
                <div className="crm-loading">
                    <div className="crm-spinner"></div>
                </div>
            ) : error ? (
                <div className="crm-empty-state">
                    <p style={{ color: 'var(--crm-danger)' }}>{error}</p>
                    <button className="crm-btn crm-btn-primary" onClick={refetch}>Retry</button>
                </div>
            ) : (!properties || properties.length === 0) ? (
                <div className="crm-empty-state">
                    <h3>No properties found</h3>
                    <p>{hasActiveFilters ? 'Try adjusting your filters' : 'Add your first property to get started'}</p>
                    {!hasActiveFilters && (
                        <button className="crm-btn crm-btn-primary" onClick={handleAdd}>
                            + Add Property
                        </button>
                    )}
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '16px',
                }}>
                    {properties.map((property) => (
                        <PropertyCard
                            key={property.id}
                            property={property}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {(total || 0) > pageSize && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '24px',
                    padding: '16px',
                    background: 'var(--crm-gray-100)',
                    border: '1px solid var(--crm-gray-200)',
                }}>
                    <span style={{
                        fontSize: '13px',
                        color: 'var(--crm-gray-600)',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                    }}>
                        Showing {startItem}-{endItem} of {total} properties
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            className="crm-btn crm-btn-secondary"
                            onClick={() => handleFilterChange('page', filters.page - 1)}
                            disabled={filters.page <= 1}
                        >
                            ← Previous
                        </button>
                        <button
                            className="crm-btn crm-btn-secondary"
                            onClick={() => handleFilterChange('page', filters.page + 1)}
                            disabled={filters.page >= totalPages}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="crm-modal-overlay" onClick={handleCloseModal}>
                    <div
                        className="crm-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'var(--crm-bg)',
                            maxWidth: '640px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            border: '1px solid var(--crm-gray-200)',
                            boxShadow: 'var(--crm-shadow-lg)',
                        }}
                    >
                        <div className="crm-modal-header">
                            <h2 className="crm-modal-title">{editingProperty ? 'Edit Property' : 'Add New Property'}</h2>
                            <button className="crm-modal-close" onClick={handleCloseModal}>?</button>
                        </div>
                        <div className="crm-modal-body">
                            <PropertyForm
                                property={editingProperty}
                                onSubmit={handleSubmit}
                                onCancel={handleCloseModal}
                                loading={mutationLoading}
                            />
                        </div>
                    </div>
                </div>
            )}
            <PropertiesAdvancedFilters
                isOpen={showAdvanced}
                filters={filters}
                onClose={() => setShowAdvanced(false)}
                onChange={handleFilterChange}
                onReset={clearFilters}
            />
            <ConfirmDialog />
        </div>
    );
}

export default PropertiesPage;
